import Modal from "@/components/controls/Modal";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import Banner from "@/components/ui/banner";
import FormNew from "@/components/controls/Form/FormNew";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import {
  FormFieldNewType,
  FieldActionProps
} from "@/components/controls/Form/types";
import {
  Close,
  AlertBubbleWarning,
  InfoCircle,
  ExternalLink,
  Organization
} from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import toast from "react-hot-toast";
import CreateNewOptionModal from "@/components/controls/CreateNewOptionModal";
import { UserData } from "@/entities/users/types";
import { CompanyData, CompanyStatus } from "../types";
import {
  useCreateCompanyMutation,
  useCheckCompanyDuplicates,
  useSearchCompaniesByName,
  DuplicateMatch
} from "../hooks";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { mapEmptyToNull } from "@/utils/formatting";
import { useEntityFields, useUpdateEntityField } from "@/pages/settings/hooks";
import {
  createFormNewSectionsFromAPI,
  CreateFormNewContext,
  buildMapEmptyToNullObject
} from "@/utils/entityFieldsUtils/formFields";
import useBoundStore from "@/services/store/BoundStore";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import UnsavedChangesWarningDialog from "@/components/layouts/UnsavedChangesWarningDialog";
import { createUnsavedChangesModalSetter } from "@/entities/modals/helpers";
import { IconButton } from "@/components/ui/icon-button";
import ToastBody from "@/components/ui/toast-body";
import { useNavigate } from "react-router-dom";
import { useSubIndustriesByIndustryId } from "@/entities/settings/hooks";
import { cn } from "@/utils/utils";
import Spinner from "@/components/ui/spinner";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import DreamerModeTabs, {
  DREAMER_MODE,
  MANUAL_MODE,
  DreamerMode
} from "@/components/controls/DreamerModeTabs";
import CreateCompanyWithDreamer from "./CreateCompanyWithDreamer";
import { useCreateCompanyWithDreamerMutation } from "../hooks";
import { isValidWebsite } from "@/utils/domains";
import { CandidateData } from "@/hooks/useOpenOnNew";
import { isAxiosError } from "axios";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  resetPage?: () => void;
  level?: number;
  /** Standalone Companies page only — embedded callers need the synchronous `onSuccess(id)` the async Dreamer path can't give. */
  allowDreamer?: boolean;
  onSuccess?: (id: string, name: string) => void;
  defaultName?: string;
  /** Prefill from a deep link, e.g. the enrichment-failure notification's manual-create CTA. */
  candidateData?: CandidateData | null;
  onCandidateDataConsumed?: () => void;
}

type FormValues = Record<string, unknown>;
type FormSetValue = (
  fieldName: string,
  value: unknown,
  options?: { shouldValidate?: boolean }
) => void;
type FormGetValues = () => FormValues;
type SelectFieldActionProps = Record<string, unknown> & {
  onSelection?: (value: number | string) => void;
  placeholder?: string;
};

const CreateCompanyModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  resetPage,
  allowDreamer = false,
  onSuccess,
  defaultName,
  candidateData,
  onCandidateDataConsumed
}) => {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  const createCompany = useCreateCompanyMutation();
  const createWithDreamer = useCreateCompanyWithDreamerMutation();
  const navigate = useNavigate();

  // `enabled` resolves async — gate the render on `loading` or the flag-OFF branch paints first and remounts the form.
  const { enabled: dreamerFlagEnabled, loading: flagLoading } = useFeatureFlag(
    FeatureFlags.CRM_8565_CREATE_COMPANY_WITH_DREAMER
  );
  // allowDreamer gates both, so embedded call sites get neither the tab nor the spinner.
  const dreamerEnabled = allowDreamer && dreamerFlagEnabled;
  const dreamerFlagLoading = allowDreamer && flagLoading;
  const [activeTab, setActiveTab] = useState<DreamerMode>(DREAMER_MODE);
  const [dreamerWebsite, setDreamerWebsite] = useState("");
  const [showDreamerValidation, setShowDreamerValidation] = useState(false);
  const [isQueueing, setIsQueueing] = useState(false);
  const isDreamerTab = dreamerEnabled && activeTab === DREAMER_MODE;

  const [formSetValue, setFormSetValue] = useState<FormSetValue | null>(null);
  const [formGetValues, setFormGetValues] = useState<FormGetValues | null>(
    null
  );
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");
  const [selectedPersonId, setSelectedPersonId] = useState<string>("");
  const [selectedIndustryId, setSelectedIndustryId] = useState<number | null>(
    null
  );

  const [websiteValue, setWebsiteValue] = useState("");
  const [debouncedWebsite, setDebouncedWebsite] = useState("");
  const [dismissedExactWarning, setDismissedExactWarning] = useState(false);
  const [dismissedSimilarWarning, setDismissedSimilarWarning] = useState(false);

  const [nameValue, setNameValue] = useState("");
  const [debouncedName, setDebouncedName] = useState("");
  const [showNameMatches, setShowNameMatches] = useState(true);

  const [showCreateOptionModal, setShowCreateOptionModal] = useState(false);
  const [newOptionName, setNewOptionName] = useState("");
  const [currentFieldForNewOption, setCurrentFieldForNewOption] = useState<{
    fieldId: string;
    fieldKey: string;
  } | null>(null);
  const [fieldResetKeys, setFieldResetKeys] = useState<Record<string, number>>(
    {}
  );

  const duplicateCheck = useCheckCompanyDuplicates(
    debouncedWebsite,
    debouncedWebsite.trim().length > 0
  );

  const nameSearch = useSearchCompaniesByName(
    debouncedName,
    debouncedName.trim().length >= 2
  );

  const { data: entityFieldsResponse } = useEntityFields(EntityType.COMPANY);
  const updateEntityField = useUpdateEntityField(EntityType.COMPANY);
  const { data: subIndustriesData } =
    useSubIndustriesByIndustryId(selectedIndustryId);

  const users = useBoundStore((state) => state.users.entities);
  const inlineFeedbackContainerClassName =
    "ml-[216px] -mt-4 w-[calc(100%-216px)]";

  const renderInlineFeedback = (
    content: React.ReactNode,
    className: string,
    dataTest: string
  ) => (
    <div className={inlineFeedbackContainerClassName}>
      <div className={className} data-test={dataTest}>
        {content}
      </div>
    </div>
  );

  const nameMatchRef = useRef<HTMLDivElement>(null);

  const renderNameMatchOverlay = (
    content: React.ReactNode,
    className: string,
    dataTest: string,
    ref?: React.RefObject<HTMLDivElement>
  ) => (
    <div
      ref={ref}
      className={cn(
        "absolute left-[216px] top-9 z-30 w-[calc(100%-216px)]",
        className
      )}
      data-test={dataTest}
    >
      {content}
    </div>
  );

  const handleCreateNewOption = async (optionName: string) => {
    if (!currentFieldForNewOption) return;

    try {
      const currentField = entityFieldsResponse?.fields?.find(
        (field) => field.id === currentFieldForNewOption.fieldId
      );

      if (!currentField?.config?.options) return;

      const currentOptions = currentField.config.options;
      const optionsArray = Array.isArray(currentOptions)
        ? currentOptions
        : Object.keys(currentOptions);

      const updatedOptionsArray = [...optionsArray, optionName];

      const updatedOptions = Array.isArray(currentOptions)
        ? updatedOptionsArray
        : updatedOptionsArray.reduce(
            (acc, option) => {
              acc[option] = option;
              return acc;
            },
            {} as Record<string, string>
          );

      await updateEntityField.mutateAsync({
        data: {
          id: currentFieldForNewOption.fieldId,
          config: {
            ...currentField.config,
            options: updatedOptions,
            allowUsersToAdd: true
          }
        },
        pathParams: {
          entityType: "companies",
          fieldId: currentFieldForNewOption.fieldId
        }
      });

      if (formSetValue) {
        const isMultiSelect = currentField.displayType === "multiSelect";

        if (isMultiSelect) {
          let currentValues: unknown[] = [];
          if (formGetValues) {
            const currentFormValues = formGetValues();
            const existingValue =
              currentFormValues[currentFieldForNewOption.fieldKey];
            currentValues = Array.isArray(existingValue) ? existingValue : [];
          }
          const newValues = Array.isArray(currentValues)
            ? [...currentValues, optionName]
            : [optionName];
          formSetValue(currentFieldForNewOption.fieldKey, newValues);
        } else {
          formSetValue(currentFieldForNewOption.fieldKey, optionName);
        }
      }

      setFieldResetKeys((prev) => ({
        ...prev,
        [currentFieldForNewOption.fieldKey]:
          (prev[currentFieldForNewOption.fieldKey] || 0) + 1
      }));

      setShowCreateOptionModal(false);
      setNewOptionName("");
      setCurrentFieldForNewOption(null);
    } catch (error) {
      console.error("Failed to create new option:", error);
    }
  };

  const formFieldsContext: CreateFormNewContext = {
    users,
    userData: {} as UserData,
    selectedCompanyId,
    selectedPersonId,
    setSelectedCompanyId,
    setSelectedPersonId,
    setHasUserEnteredDealName: () => {},
    autoPopulateDealName: () => {},
    hasUserEnteredDealName: false,
    setView: () => {},
    entityType: EntityType.COMPANY,
    showCreateOptionModal,
    newOptionName,
    currentFieldForNewOption,
    fieldResetKeys,
    setShowCreateOptionModal,
    setNewOptionName,
    setCurrentFieldForNewOption,
    handleCreateNewOption
  };

  const baseSections = createFormNewSectionsFromAPI(
    entityFieldsResponse?.fields,
    t,
    formFieldsContext
  );

  const subIndustryOptions = useMemo(() => {
    if (!subIndustriesData) return [];
    // Hidden values are not selectable when creating a company.
    return subIndustriesData
      .filter((subIndustry) => !subIndustry.isHidden)
      .map((subIndustry) => ({
        id: subIndustry.id,
        displayName: subIndustry.name
      }));
  }, [subIndustriesData]);

  // Derived duplicate state
  const normalizedWebsite = websiteValue.trim();
  const isDuplicateCheckPending =
    normalizedWebsite.length > 0 &&
    normalizedWebsite !== debouncedWebsite.trim();
  const canUseDuplicateResults =
    normalizedWebsite.length > 0 && !isDuplicateCheckPending;

  const exactMatches = canUseDuplicateResults
    ? (duplicateCheck.data?.exactMatches ?? [])
    : [];
  const similarMatches = canUseDuplicateResults
    ? (duplicateCheck.data?.similarMatches ?? [])
    : [];
  const hasExactMatch = exactMatches.length > 0 && !dismissedExactWarning;
  // The backend examines one capped page of domain candidates. When more matched
  // than fit on it, the lists below are a partial view and their length is a lower
  // bound on the real number of companies — so the copy has to say "at least".
  const duplicatesTruncated = canUseDuplicateResults
    ? (duplicateCheck.data?.truncated ?? false)
    : false;

  const isCheckingDuplicates =
    isDuplicateCheckPending ||
    (duplicateCheck.isFetching && debouncedWebsite.trim().length > 0);

  // Name matching state
  const nameMatches = nameSearch.data?.companies ?? [];
  // The dropdown lists at most five companies; the query's total is how many
  // actually match, and that is the number the heading reports.
  const nameMatchTotal = nameSearch.data?.total ?? nameMatches.length;
  const hasNameMatches =
    showNameMatches &&
    debouncedName.trim().length >= 2 &&
    nameMatches.length > 0;

  // Reset dismissals when website changes
  useEffect(() => {
    setDismissedExactWarning(false);
    setDismissedSimilarWarning(false);
  }, [websiteValue]);

  // Reset name match visibility when name changes
  useEffect(() => {
    setShowNameMatches(true);
  }, [nameValue]);

  // Dismiss name match overlay on Escape key or click outside
  useEffect(() => {
    if (!hasNameMatches) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setShowNameMatches(false);
      }
    };

    const handleMouseDown = (e: MouseEvent) => {
      if (
        nameMatchRef.current &&
        !nameMatchRef.current.contains(e.target as Node)
      ) {
        setShowNameMatches(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleMouseDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, [hasNameMatches]);

  const nameMatchDropdown = hasNameMatches
    ? renderNameMatchOverlay(
        <>
          <div className="border-b border-[#26374b] px-3 py-2">
            <Text variant="smBodySecondary">
              {nameMatchTotal === 1
                ? t("companies.createModal.existingCompanyMatch")
                : t("companies.createModal.existingCompaniesMatch", {
                    count: nameMatchTotal
                  })}
            </Text>
          </div>
          <div className="flex flex-col">
            {nameMatches.map((company, index) => (
              <div
                key={company.id}
                className={cn(
                  "flex min-h-9 items-center justify-between gap-3 px-3 py-2 hover:bg-[#233142]",
                  index === 0 && "bg-[#202c3a]",
                  index !== nameMatches.length - 1 &&
                    "border-b border-[#26374b]"
                )}
              >
                <div className="flex min-w-0 items-center gap-2.5">
                  <IconContainer
                    as="span"
                    size="5"
                    color="secondary"
                    nativeStroke
                    className="shrink-0"
                  >
                    <Organization />
                  </IconContainer>
                  <Text variant="body" className="truncate">
                    {company.name}
                  </Text>
                </div>
                <Button
                  type="button"
                  variant="link"
                  className="h-auto shrink-0 text-natural opacity-90 hover:opacity-100"
                  onClick={() => {
                    setShowNameMatches(false);
                    setIsOpen(false);
                    navigate(`/companies/${company.id}`);
                  }}
                >
                  {t("companies.createModal.open")}
                </Button>
              </div>
            ))}
          </div>
        </>,
        "overflow-hidden rounded-lg border border-[#2a3b50] bg-[#172231] shadow-[0_8px_16px_-10px_rgba(0,0,0,0.75)]",
        "name-match-dropdown",
        nameMatchRef
      )
    : null;

  const domainWarning = (() => {
    if (isCheckingDuplicates) {
      return renderInlineFeedback(
        <div className="px-3 py-2">
          <Text variant="smBodySecondary">
            {t("companies.createModal.duplicateChecking")}
          </Text>
        </div>,
        "rounded-lg border border-[#2a3b50] bg-[#172231] shadow-none",
        "duplicate-checking"
      );
    }

    if (exactMatches.length > 0 && !dismissedExactWarning) {
      const firstMatch = exactMatches[0];
      return (
        <div
          className={inlineFeedbackContainerClassName}
          data-test="duplicate-exact-warning"
        >
          <Banner variant="warning">
            <div className="flex flex-col gap-3 px-3 py-2.5">
              <div className="flex items-start gap-2">
                <IconContainer
                  as="span"
                  size="5"
                  color="warning"
                  nativeStroke
                  className="mt-0.5 shrink-0"
                >
                  <AlertBubbleWarning />
                </IconContainer>
                <Text
                  variant="bodySecondary"
                  className="leading-5 text-primary"
                >
                  {exactMatches.length === 1
                    ? t(
                        duplicatesTruncated
                          ? "companies.createModal.duplicateExactSingleTruncated"
                          : "companies.createModal.duplicateExactSingle",
                        {
                          domain: firstMatch.domain,
                          companyName: firstMatch.companyName
                        }
                      )
                    : t(
                        duplicatesTruncated
                          ? "companies.createModal.duplicateExactMultipleTruncated"
                          : "companies.createModal.duplicateExactMultiple",
                        {
                          domain: firstMatch.domain,
                          count: exactMatches.length
                        }
                      )}
                </Text>
              </div>
              <div className="flex w-full items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setDismissedExactWarning(true)}
                  data-test="use-domain-anyway-button"
                >
                  {t("companies.createModal.useThisDomainAnyway")}
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => {
                    setIsOpen(false);
                    navigate(`/companies/${firstMatch.companyId}`);
                  }}
                  data-test="open-exact-match-button"
                >
                  <IconContainer
                    as="span"
                    nativeStroke
                    className="mr-1 inline-flex"
                  >
                    <ExternalLink />
                  </IconContainer>
                  {t("companies.createModal.open")} {firstMatch.companyName}
                </Button>
              </div>
            </div>
          </Banner>
        </div>
      );
    }

    if (similarMatches.length > 0 && !dismissedSimilarWarning) {
      return renderInlineFeedback(
        <>
          <div className="flex min-h-8 items-center justify-between gap-3 px-3 py-2">
            <div className="flex min-w-0 items-center gap-2">
              <IconContainer
                as="span"
                color="natural"
                nativeStroke
                className="shrink-0"
              >
                <InfoCircle />
              </IconContainer>
              <Text variant="bodySecondary" className="leading-5 text-primary">
                {similarMatches.length === 1
                  ? t(
                      duplicatesTruncated
                        ? "companies.createModal.duplicateSimilarSingleTruncated"
                        : "companies.createModal.duplicateSimilarSingle"
                    )
                  : t(
                      duplicatesTruncated
                        ? "companies.createModal.duplicateSimilarMultipleTruncated"
                        : "companies.createModal.duplicateSimilarMultiple",
                      { count: similarMatches.length }
                    )}
              </Text>
            </div>
            <IconButton
              type="button"
              size="xs"
              variant="iconNoBg"
              className="shrink-0 text-secondary hover:bg-[#253866]"
              onClick={() => setDismissedSimilarWarning(true)}
              data-test="dismiss-similar-warning"
            >
              <Close />
            </IconButton>
          </div>
          <div className="flex flex-col gap-1.5 px-2 pb-2">
            {similarMatches.map((match: DuplicateMatch) => (
              <div
                key={match.companyId}
                className="flex min-h-10 items-center justify-between gap-3 rounded-md bg-[#24345f] px-3 py-2"
              >
                <div className="flex min-w-0 items-center gap-2.5">
                  <IconContainer
                    as="span"
                    size="5"
                    color="secondary"
                    nativeStroke
                    className="shrink-0"
                  >
                    <Organization />
                  </IconContainer>
                  <div className="min-w-0">
                    <Text variant="body" className="truncate">
                      {match.companyName}
                    </Text>
                    <Text
                      variant="smBodySecondary"
                      className="block truncate text-secondary"
                    >
                      {match.domain}
                    </Text>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="link"
                  className="h-auto shrink-0 text-natural opacity-90 hover:opacity-100"
                  onClick={() => {
                    setIsOpen(false);
                    navigate(`/companies/${match.companyId}`);
                  }}
                >
                  {t("companies.createModal.open")}
                </Button>
              </div>
            ))}
          </div>
        </>,
        "overflow-hidden rounded-lg border border-[#1e4f9d] bg-[#142a58] shadow-none",
        "duplicate-similar-warning"
      );
    }

    return null;
  })();

  // Transform fields with custom props (industry, subIndustry, name default)
  const transformField = (field: FormFieldNewType): FormFieldNewType => {
    const baseFieldProps: FormFieldNewType = {
      ...field,
      labelPosition: "left" as const,
      layout: "flex" as const,
      ...(field.name === "name" && defaultName
        ? { defaultValue: defaultName }
        : {})
    };

    if (field.name === "industryId") {
      const existingProps = field.fieldActionProps as SelectFieldActionProps;
      const existingOnSelection = existingProps?.onSelection;
      return {
        ...baseFieldProps,
        fieldActionProps: {
          ...existingProps,
          showSearch: true,
          onSelection: (value: number | string) => {
            const numericValue =
              typeof value === "number"
                ? value
                : value
                  ? parseInt(value, 10)
                  : null;
            setSelectedIndustryId(numericValue);
            if (formSetValue) {
              formSetValue("subIndustryId", "");
            }
            existingOnSelection?.(value);
          }
        } as FieldActionProps
      };
    }

    if (field.name === "subIndustryId") {
      const isDisabled = selectedIndustryId === null;
      const existingProps = field.fieldActionProps as SelectFieldActionProps;
      return {
        ...baseFieldProps,
        fieldActionProps: {
          ...existingProps,
          options: subIndustryOptions,
          disabled: isDisabled,
          placeholder: isDisabled ? t("select") : existingProps?.placeholder,
          showSearch: true
        } as FieldActionProps
      };
    }

    return baseFieldProps;
  };

  // Split sections to inject inline warnings after name and website fields
  const formSections: FormSectionProps[] = baseSections.flatMap((section) => {
    const fields = section.fields ?? [];
    const nameIndex = fields.findIndex((f) => f.name === "name");
    const websiteIndex = fields.findIndex((f) => f.name === "website");

    if (nameIndex === -1 && websiteIndex === -1) {
      return [
        {
          ...section,
          fields: fields.map(transformField)
        }
      ];
    }

    // Split fields into groups at name/website boundaries
    const splitPoints: {
      afterIndex: number;
      content: React.ReactNode;
      isOverlay?: boolean;
    }[] = [];
    if (nameIndex !== -1) {
      splitPoints.push({
        afterIndex: nameIndex,
        content: nameMatchDropdown,
        isOverlay: true
      });
    }
    if (websiteIndex !== -1) {
      splitPoints.push({ afterIndex: websiteIndex, content: domainWarning });
    }
    splitPoints.sort((a, b) => a.afterIndex - b.afterIndex);

    const subSections: FormSectionProps[] = [];
    let currentStart = 0;

    for (const { afterIndex, content, isOverlay } of splitPoints) {
      const endIndex = afterIndex + 1;
      const subFields = fields
        .slice(currentStart, endIndex)
        .map(transformField);
      subSections.push({
        id: `${section.id}-${currentStart}`,
        fields: subFields,
        bottomCustomComponent: content,
        sectionClassName: isOverlay ? "relative z-20" : undefined
      });
      currentStart = endIndex;
    }

    if (currentStart < fields.length) {
      subSections.push({
        id: `${section.id}-${currentStart}`,
        fields: fields.slice(currentStart).map(transformField)
      });
    }

    return subSections;
  });

  // Both tabs stay mounted, so closing checks both; distinct key so the form's own `website` field isn't clobbered.
  const { handleCloseAttempt, warningDialogProps } = useUnsavedChanges(
    () => ({
      ...(formGetValues?.() || {}),
      ...(dreamerWebsite.trim() ? { dreamerWebsite } : {})
    }),
    formSections
  );

  useEffect(() => {
    if (!websiteValue.trim()) {
      setDebouncedWebsite("");
      return;
    }
    const timer = setTimeout(() => {
      setDebouncedWebsite(websiteValue);
    }, 500);
    return () => clearTimeout(timer);
  }, [websiteValue]);

  useEffect(() => {
    if (!nameValue.trim() || nameValue.trim().length < 2) {
      setDebouncedName("");
      return;
    }
    const timer = setTimeout(() => {
      setDebouncedName(nameValue);
    }, 500);
    return () => clearTimeout(timer);
  }, [nameValue]);

  useEffect(() => {
    if (defaultName && typeof formSetValue === "function") {
      formSetValue("name", defaultName, { shouldValidate: true });
    }
  }, [defaultName, formSetValue]);

  const candidateWebsite = candidateData?.website;

  // Deep links only come from the enrichment-failure notification — retrying Dreamer would fail the same way.
  useEffect(() => {
    if (isOpen && candidateWebsite) {
      setActiveTab(MANUAL_MODE);
    }
  }, [isOpen, candidateWebsite]);

  useEffect(() => {
    if (isOpen && candidateWebsite && typeof formSetValue === "function") {
      formSetValue("website", candidateWebsite, { shouldValidate: true });
    }
  }, [isOpen, candidateWebsite, formSetValue]);

  useEffect(() => {
    if (isOpen) {
      setIsSubmitting(false);
    } else {
      formRef.current?.reset();
      setFormSetValue(null);
      setFormGetValues(null);
      setSelectedCompanyId("");
      setSelectedPersonId("");
      setSelectedIndustryId(null);
      setShowCreateOptionModal(false);
      setNewOptionName("");
      setCurrentFieldForNewOption(null);
      setFieldResetKeys({});
      setIsFormValid(false);
      setWebsiteValue("");
      setDebouncedWebsite("");
      setDismissedExactWarning(false);
      setDismissedSimilarWarning(false);
      setNameValue("");
      setDebouncedName("");
      setShowNameMatches(true);
      setActiveTab(DREAMER_MODE);
      setDreamerWebsite("");
      setShowDreamerValidation(false);
      setIsQueueing(false);
      // Drop the deep-link prefill so reopening the modal starts clean.
      onCandidateDataConsumed?.();
    }
    // onCandidateDataConsumed omitted: a fresh closure per parent render would re-run the reset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Stable backend codes; 403/404 are dead ends and must not read as transient (404 = backend flag off).
  const dreamerQueueErrorTitle = (error: unknown): string => {
    const detail = isAxiosError(error) ? error.response?.data?.detail : undefined;
    switch (detail) {
      case "user_not_provisioned":
        return t("companies.createModal.withDreamer.errorNotProvisioned");
      case "invalid_website":
        return t("companies.createModal.withDreamer.invalidUrl");
      case "not_found":
        return t("companies.createModal.withDreamer.errorNotAvailable");
      default:
        return t("companies.createModal.withDreamer.queueError");
    }
  };

  const handleDreamerSubmit = async () => {
    setShowDreamerValidation(true);
    if (!isValidWebsite(dreamerWebsite) || isQueueing) {
      return;
    }
    setIsQueueing(true);
    // mutateAsync rejects even with onError supplied — unguarded, the toast never renders.
    try {
      await createWithDreamer.mutateAsync({ website: dreamerWebsite.trim() });
      toast.success(
        <ToastBody title={t("companies.createModal.withDreamer.queued")} />,
        { duration: 5000 }
      );
      setIsOpen(false);
    } catch (error) {
      setIsQueueing(false);
      toast.error(
        <ToastBody
          title={dreamerQueueErrorTitle(error)}
          actions={
            <IconButton
              size="md"
              variant="iconNoBg"
              iconColor="secondary"
              onClick={() => toast.dismiss()}
            >
              <Close />
            </IconButton>
          }
        />,
        { icon: null }
      );
    }
  };

  const handleSubmit = async (data: CompanyData) => {
    if (isSubmitting || isCheckingDuplicates || hasExactMatch) {
      return;
    }
    setIsSubmitting(true);
    const transformFormData: CompanyData = {
      ...data,
      ...mapEmptyToNull(buildMapEmptyToNullObject(baseSections, data)),
      status: CompanyStatus.PROSPECT
    };

    await createCompany.mutateAsync(transformFormData, {
      onSuccess: async (response) => {
        await resetPage?.();
        onSuccess?.(response.id, data.name);
        toast.success(
          <ToastBody
            title={`${data.name} ${t("companies.createModal.success")}`}
            actions={
              <Button
                className="size-fit p-1"
                onClick={() => {
                  toast.dismiss();
                  navigate(`/companies/${response?.id}`);
                }}
                variant="primary"
              >
                {t("companies.createModal.view")}
              </Button>
            }
          />,
          {
            duration: 5000
          }
        );
        setIsOpen(false);
      },
      onError: () => {
        setIsSubmitting(false);
        toast.error(
          <ToastBody
            title={t("companies.createModal.error")}
            actions={
              <IconButton
                size="md"
                variant="iconNoBg"
                iconColor="secondary"
                onClick={() => toast.dismiss()}
              >
                <Close />
              </IconButton>
            }
          />,
          {
            icon: null
          }
        );
      }
    });
  };

  const handleCancel = () => {
    handleCloseAttempt(() => {
      setIsOpen(false);
    });
  };

  const handleCreateNew = () => {
    formRef.current?.requestSubmit();
  };

  const handleSetValue = (formMethods: {
    setValue: FormSetValue;
    getValues: FormGetValues;
  }) => {
    if (typeof formMethods.setValue === "function") {
      setFormSetValue(() => formMethods.setValue);
    }
    if (typeof formMethods.getValues === "function") {
      setFormGetValues(() => formMethods.getValues);
    }
  };

  return (
    <>
      <Modal
        open={isOpen}
        setIsOpen={createUnsavedChangesModalSetter(
          setIsOpen,
          handleCloseAttempt
        )}
        data-test="create-company-modal"
        title={
          <Text as="div" variant="lgSubtitleH1">
            {t("companies.createModal.title")}
          </Text>
        }
        content={
          <div
            className={cn(
              "flex flex-col p-6 overflow-y-auto",
              // g6 (24px) between the switch and the Manually form; the Dreamer tab keeps its own rhythm.
              isDreamerTab ? "gap-3" : "gap-6",
              isDreamerTab ? "max-h-[60vh]" : "h-[60vh]"
            )}
            data-test="create-company-modal-content"
          >
            {dreamerEnabled && !dreamerFlagLoading ? (
              <DreamerModeTabs
                labels={{
                  withDreamer: t("dreamerCreate.withDreamerTab"),
                  manually: t("dreamerCreate.manuallyTab")
                }}
                value={activeTab}
                onChange={setActiveTab}
                dataTest="create-company-modal-tabs"
              />
            ) : null}

            {dreamerFlagLoading ? (
              <div className="flex h-32 items-center justify-center">
                <Spinner />
              </div>
            ) : (
              <>
                {isDreamerTab ? (
                  <CreateCompanyWithDreamer
                    website={dreamerWebsite}
                    setWebsite={setDreamerWebsite}
                    showValidation={showDreamerValidation}
                    onClose={() => setIsOpen(false)}
                  />
                ) : null}
                {/* Hidden, not unmounted: remounting react-hook-form wipes typed values on a tab peek. */}
                <div className={cn(isDreamerTab && "hidden")}>
                  <FormNew
                    sections={formSections}
                    isFormValid={(isValid, watchAllFields) => {
                      setIsFormValid(isValid);
                      const currentWebsite =
                        (watchAllFields?.website as string) || "";
                      if (currentWebsite !== websiteValue) {
                        setWebsiteValue(currentWebsite);
                      }
                      const currentName = (watchAllFields?.name as string) || "";
                      if (currentName !== nameValue) {
                        setNameValue(currentName);
                      }
                    }}
                    onSubmit={handleSubmit}
                    formRef={formRef}
                    onSetValue={handleSetValue}
                    data-test="create-company-modal-form"
                  />
                </div>
              </>
            )}
          </div>
        }
        footer={
          <div
            className="flex justify-end gap-2"
            data-test="create-company-modal-footer"
          >
            <Button
              data-test="create-company-modal-cancel-button"
              variant="secondary"
              onClick={handleCancel}
            >
              {t("cancel")}
            </Button>
            {isDreamerTab ? (
              <Button
                data-test="create-company-modal-dreamer-submit-button"
                disabled={isQueueing || dreamerWebsite.trim().length === 0}
                onClick={handleDreamerSubmit}
              >
                {t("companies.createModal.withDreamer.submit")}
              </Button>
            ) : (
              <Button
                data-test="create-company-modal-submit-button"
                disabled={
                  !isFormValid ||
                  isSubmitting ||
                  isCheckingDuplicates ||
                  hasExactMatch
                }
                onClick={handleCreateNew}
              >
                {t("companies.createModal.createNew")}
              </Button>
            )}
          </div>
        }
        contentContainerClassname="w-[640px]"
      />
      <CreateNewOptionModal
        isOpen={showCreateOptionModal}
        onClose={() => setShowCreateOptionModal(false)}
        onCreateOption={handleCreateNewOption}
        title={t("createNewOption.title", { optionName: newOptionName })}
        description={t("createNewOption.description")}
        createButtonText={t("createNewOption.create")}
        cancelButtonText={t("cancel")}
        optionName={newOptionName}
        data-test="create-company-modal-new-option"
      />
      <UnsavedChangesWarningDialog
        {...warningDialogProps}
        data-test="create-company-modal-unsaved-warning"
      />
    </>
  );
};

export default CreateCompanyModal;
